// components/account/Navbar.jsx - ULTRA FIXED version with NO infinite loops

"use client";

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { createPortal } from "react-dom";
import SearchModal from "../common/SearchModal";
import LogoutConfirmationModal from "../modals/LogoutConfirmModal";
import NotificationDropdown from "./NotificationDropdown";
import ChatDropdown from "./ChatDropdown";
import SupportModal from "../modals/SupportModal";
import AIChatWidget from "../chat/AIChatWidget";
import { useAuth } from "../../hooks/useAuth"; // UNIFIED AUTHENTICATION
import { handleApiError } from "../../utils/apiUtils";
import { getUserPlaceholderImage } from "../../utils/imageUtils";
import { useTranslation } from "react-i18next";
import { useHamburgerMenu } from "../../hooks/useHamburgerMenu";
import api from "../../services/api";
import { notificationsAPI, userAPI } from "../../services/api";
import "./Navbar.css";
import { USER_TYPES } from "../../constants/userTypes";

// Language Toggle Component
const LanguageToggle = () => {
  const { i18n, t } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);

  const toggleLanguage = () => {
    const newLang = i18n.language === "en" ? "ar" : "en";
    i18n.changeLanguage(newLang);
  };

  return (
    <button
      onClick={toggleLanguage}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`navbar-language-toggle ${isHovered ? 'navbar-language-toggle-hovered' : ''}`}
      title={
        i18n.language === "en"
          ? t("language.switchToArabic")
          : t("language.switchToEnglish")
      }
    >
      {i18n.language === "en" ? "AR" : "EN"}
    </button>
  );
};

// Icon Component - same as before
const IconComponent = ({
  iconName,
  isActive,
  isHovered,
  colors,
  defaultColor = "rgba(73, 73, 73, 0.6)",
}) => {
  const iconColor = isHovered || isActive ? colors.primary : defaultColor;

  const getIconSvg = (name, color) => {
    switch (name) {
      case "social-media":
        return (
          <svg
            width="28"
            height="28"
            viewBox="350 450 450 200"
            fill={color}
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M392.7,530.23c1.58-15.11,10.23-36.43,21.93-46.45,24.95-21.37,69.48-10.23,86.04,16.57,2.42,3.92,9.19,17.15,8.41,21.18-5.61,12.01-12.94,26.17-14.86,39.41-1.02,7.06-.46,9.95,5.04,14.47,24.06,19.78,73.77,41.23,103.85,49.91,62.66,18.08,125.68,12.55,188.92.44,12.19,2.62,22.48,17.55,9.1,26.02-9.02,5.7-27.75,14.01-38.05,18.6-7.44,3.31-15.07,6.25-22.85,8.71-.93,16.27,5.13,33.76-11.75,44.09-7.77,4.75-33.64,5.35-42,2.48-11.76-4.03-18.25-16.09-16.92-28.22.23-2.15,3.85-12.73,3.15-13.43-18.21-1.09-36.37-1.99-54.37-5.11-16.26-2.83-32.31-7.34-47.59-13.5-1.23,15.79,5.78,39.01-4.88,52.18-2.95,3.64-9.71,8.35-14.34,9.13-8.38,1.41-33.17,1.05-40.4-2.91-23.15-12.68-8.25-42.78-6.85-62.96,2.73-39.44-35.29-59.83-62.76-80.27-13.5-10.04-31.71-20.68-43.89-31.37-2.24-1.97-3.3-3.6-4.93-6,.31-4.17-.43-8.86,0-12.95Z" />
            <path d="M601.49,484.92l-6.94,36.35-41.99,56.78c-1.74,1.35-6.9-2.03-9.07-3.17-8.32-4.36-16.2-9.78-24.19-14.71,9.44-37.91,43.48-69.29,82.19-75.25Z" />
            <path d="M696.86,497.98l-5.72,34.08-2.05,2-36.09,6.45-33.76-20.53c.98-4.48,6.01-37.58,7.86-38.2,2.04-.68,16.94-.33,20.11-.1,17.04,1.21,34.88,8.07,49.65,16.3Z" />
            <path d="M637.74,558.72l-4.68,49.2c-20.39-3.04-39.81-10.73-59.07-17.82l35.32-48.46,28.44,17.07Z" />
            <path d="M767.37,606.28c-9.54.64-20.14,3.7-29.56,4.1-1.18.05-2.39.22-3.21-.83l-21.38-66.08,4.81-27.8c.77-.83,3.05,1.77,3.65,2.41,18.3,19.79,32.13,44.52,40.87,70,.86,2.5,5.79,17.18,4.82,18.2Z" />
            <path d="M692.84,557.84l17.9,54.93c-18.09.67-36.22,1.67-54.21-.81l5.2-48.6,31.11-5.52Z" />
          </svg>
        );
      case "my-pets":
        return (
          <svg
            width="26"
            height="26"
            viewBox="450 320 400 400"
            fill={color}
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M759.57,460.15c0-19.36-15.22-27.44-25.42-27.44h-30.42v25.43c0,18.05-14.63,21.39-23.56,22.32-51.4-2.47-106.54,3.71-157.51.17-9.73-.68-26.39-1.18-26.39-22.49v-25.43h-30.42c-10.2,0-25.42,9.32-25.42,27.44l-.05,335.15c1.31,15.11,14.21,27.73,29.42,28.43h261.4c14.78-1.29,27.15-13.66,28.43-28.43l-.05-335.15ZM679.45,545.82c15.58,15.51-6.75,52.66-25.94,41.4-23.07-13.53,6.34-60.92,25.94-41.4ZM598.79,509.79c26.03-2.08,27.81,46.88,1.61,47.57-24.86.66-26.97-45.55-1.61-47.57ZM564.91,612.55c7.25-10.09,14.66-29.79,27.85-31.99,27.43-4.58,30.01,15.01,42.76,32.57,8.96,12.34,26.27,23.16,14.79,39.94-12.66,18.5-28.79,7.85-44.34,3.59-12.8-3.51-23.81,7.38-37.21,7.06-12.96-.3-24.65-12.74-22.54-25.98.98-6.13,14.28-19.02,18.7-25.18ZM520.55,545.82c19.61-19.52,49.02,27.87,25.94,41.4-19.2,11.26-41.52-25.9-25.94-41.4ZM692.35,761.02c0,5.74-4.7,10.44-10.44,10.44h-162.82c-5.74,0-10.44-4.7-10.44-10.44h0c0-5.74,4.7-10.44,10.44-10.44h162.82c5.74,0,10.44,4.7,10.44,10.44h0ZM692.35,716.49c0,5.74-4.7,10.44-10.44,10.44h-162.82c-5.74,0-10.44-4.7-10.44-10.44h0c0-5.74,4.7-10.44,10.44-10.44h162.82c5.74,0,10.44,4.7,10.44,10.44h0Z" />
            <path d="M647.87,408.78c1.2,1.23,29.29-.96,34.39.02.89.17,1.65.6,2.32,1.16,1.74,1.44,2.67,3.64,2.7,5.9l.54,42.35c-.02,1.44-.67,2.65-1.58,3.68-1.5,1.69-3.73,2.54-5.99,2.55l-161.6.25c-1.43-.02-2.63-.66-3.66-1.56-1.71-1.51-2.56-3.77-2.55-6.06l.28-41.18c.02-2.28.94-4.5,2.7-5.94.67-.55,1.42-.97,2.3-1.14,5.11-.97,33.2,1.22,34.39-.02-.96-12.98,8.84-26.48,20.97-30.4,8.37-2.71,35.65-2.29,45.4-1.57,16.77,1.25,29.93,14.92,29.38,31.97Z" />
          </svg>
        );
      case "marketplace":
        return (
          <svg
            width="28"
            height="28"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M10 10.8333C8.89493 10.8333 7.83512 10.3943 7.05372 9.61286C6.27232 8.83146 5.83333 7.77165 5.83333 6.66658H7.5C7.5 7.32963 7.76339 7.96551 8.23223 8.43435C8.70107 8.90319 9.33696 9.16658 10 9.16658C10.663 9.16658 11.2989 8.90319 11.7678 8.43435C12.2366 7.96551 12.5 7.32963 12.5 6.66658H14.1667C14.1667 7.77165 13.7277 8.83146 12.9463 9.61286C12.1649 10.3943 11.1051 10.8333 10 10.8333ZM10 2.49992C10.663 2.49992 11.2989 2.76331 11.7678 3.23215C12.2366 3.70099 12.5 4.33688 12.5 4.99992H7.5C7.5 4.33688 7.76339 3.70099 8.23223 3.23215C8.70107 2.76331 9.33696 2.49992 10 2.49992ZM15.8333 4.99992H14.1667C14.1667 4.45274 14.0589 3.91093 13.8495 3.4054C13.6401 2.89988 13.3332 2.44055 12.9463 2.05364C12.5594 1.66673 12.1 1.35982 11.5945 1.15042C11.089 0.941026 10.5472 0.833252 10 0.833252C8.89493 0.833252 7.83512 1.27224 7.05372 2.05364C6.27232 2.83504 5.83333 3.89485 5.83333 4.99992H4.16667C3.24167 4.99992 2.5 5.74159 2.5 6.66658V16.6666C2.5 17.1086 2.67559 17.5325 2.98816 17.8451C3.30072 18.1577 3.72464 18.3333 4.16667 18.3333H15.8333C16.2754 18.3333 16.6993 18.1577 17.0118 17.8451C17.3244 17.5325 17.5 17.1086 17.5 16.6666V6.66658C17.5 5.74159 16.75 4.99992 15.8333 4.99992Z"
              fill={color}
            />
          </svg>
        );
      case "medical-care-vet":
        return (
          <svg
            width="28"
            height="28"
            viewBox="435 405 400 400"
            fill={color}
          >
            <path d="M487.32,430.18v28.5h-28.5v61.98c0,28.35,32.3,52.25,59.36,50.74,24.79-1.38,53.33-24.73,53.33-50.74v-61.98h-28.5v-28.5h46.4c5.42,2.21,9.84,6.09,10.61,12.26-3.08,48.01,12.61,98.74-27.17,135.24-38.3,35.13-99.19,27.88-128.22-14.98-7.77-11.47-11.93-24.13-14.31-37.72v-84.19c2.07-5.23,5.35-8.53,10.61-10.61h46.4Z" />
            <path d="M529.74,613.8v56.67c7.49,81.9,123.72,97.12,150.48,18.58,2.9-8.52,3.88-16.6,4.63-25.53h28.5c.18,50.55-36.58,95.03-86.14,104.44-48.64,9.23-97.72-16.48-117.48-61.57-12.95-29.55-8.37-61.13-9.17-92.59h29.17Z" />
            <path d="M692.66,557.64c52.13-5.86,67.29,64.32,22.2,81.86-33.46,13.01-66.89-17.64-56.69-52.05,4.44-14.99,18.78-28.04,34.49-29.81Z" />
            <path d="M737.65,533.16c3.91,3.93,4.98,10.36,3.03,15.49-4.54,11.93-22.28,11.9-26.81,0-5.59-14.7,12.79-26.54,23.77-15.49Z" />
            <path d="M652.8,581.74c-10.98,11.05-29.36-.78-23.77-15.49,4.52-11.9,22.27-11.93,26.81,0,1.95,5.12.87,11.55-3.03,15.49Z" />
            <path d="M668.11,529.11c19.42-3.27,22.65,25.3,5.65,28.18-20.31,3.45-23.41-25.2-5.65-28.18Z" />
            <path d="M752.29,557.61c20.42-3.47,23.59,25.7,5.64,28.17-19.5,2.69-22.36-25.33-5.64-28.17Z" />
          </svg>
        );
      case "medical-care-not-vet":
        return (
          <svg
            width="28"
            height="28"
            viewBox="410 385 450 450"
            fill={color}
          >
            <path d="M528.54,430.42c25.92-1.46,50.26,9.51,71.37,23.43,1.58.15,12.73-7.59,15.34-8.99,99.73-53.46,167.58,51.53,152.25,142.72-11.54,68.61-83.16,149.01-146.14,176.3-18.28,7.92-24.74,7.73-42.91-.25-66.35-29.14-139.5-113.47-147.33-187.01-6.5-61.06,27.87-142.27,97.42-146.19ZM564.23,519.33c-22.67,3.87-16.46,58.61,11.54,52.83,1.19-.25,2.67-1.17,3.7-1.86,18.42-12.29,8.71-55.06-15.24-50.98ZM629.35,519.32c-9.9,1.69-17.12,15.67-18.05,24.83-1.44,14.23,6.49,33.93,23.81,26.99,21.11-8.46,20.27-56.26-5.77-51.82ZM517.41,555.85c-17.86,1.95-16.18,27.74-8.89,39.46,13.04,20.96,37.35,15.48,35.26-10.64-1-12.45-12.29-30.35-26.37-28.81ZM679.42,555.85c-26.53,2.91-32.52,58.1-2.96,51.88,24.24-5.11,29.23-54.76,2.96-51.88ZM597.63,592.44c-44.16,2.19-77.73,61.7-23.21,75.94,27.8,7.26,91,3.65,77.94-39.82-6.57-21.87-32.42-37.23-54.73-36.12Z" />
          </svg>
        );
      case "appointments":
        return (
          <svg
            width="28"
            height="28"
            viewBox="0 0 16 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M5.5 8.33325V9.99992H3.83333V8.33325H5.5ZM8.83333 8.33325V9.99992H7.16667V8.33325H8.83333ZM12.1667 8.33325V9.99992H10.5V8.33325H12.1667ZM13.8333 2.49992C14.2754 2.49992 14.6993 2.67551 15.0118 2.98807C15.3244 3.30063 15.5 3.72456 15.5 4.16659V15.8333C15.5 16.2753 15.3244 16.6992 15.0118 17.0118C14.6993 17.3243 14.2754 17.4999 13.8333 17.4999H2.16667C1.24167 17.4999 0.5 16.7499 0.5 15.8333V4.16659C0.5 3.72456 0.675595 3.30063 0.988155 2.98807C1.30072 2.67551 1.72464 2.49992 2.16667 2.49992H3V0.833252H4.66667V2.49992H11.3333V0.833252H13V2.49992H13.8333ZM13.8333 15.8333V6.66658H2.16667V15.8333H13.8333ZM5.5 11.6666V13.3333H3.83333V11.6666H5.5ZM8.83333 11.6666V13.3333H7.16667V11.6666H8.83333ZM12.1667 11.6666V13.3333H10.5V11.6666H12.1667Z"
              fill={color}
            />
          </svg>
        );
      case "account-preferences":
        return (
          <svg
            width="28"
            height="28"
            viewBox="0 0 19 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M7.33464 0.333252C6.45058 0.333252 5.60273 0.684441 4.97761 1.30956C4.35249 1.93468 4.0013 2.78253 4.0013 3.66659C4.0013 4.55064 4.35249 5.39849 4.97761 6.02361C5.60273 6.64873 6.45058 6.99992 7.33464 6.99992C8.21869 6.99992 9.06654 6.64873 9.69166 6.02361C10.3168 5.39849 10.668 4.55064 10.668 3.66659C10.668 2.78253 10.3168 1.93468 9.69166 1.30956C9.06654 0.684441 8.21869 0.333252 7.33464 0.333252ZM13.168 6.99992C13.0596 6.99992 12.968 7.07492 12.9513 7.17492L12.793 8.27492C12.543 8.38325 12.3013 8.51659 12.0846 8.66658L11.0513 8.24992C10.9596 8.24992 10.8513 8.24992 10.793 8.35825L9.95963 9.79992C9.90963 9.89158 9.9263 9.99992 10.0096 10.0666L10.893 10.7499C10.8763 10.8916 10.868 11.0249 10.868 11.1666C10.868 11.3083 10.8763 11.4416 10.893 11.5833L10.0096 12.2666C9.93464 12.3333 9.90963 12.4416 9.95963 12.5333L10.793 13.9749C10.843 14.0833 10.9513 14.0833 11.0513 14.0833L12.0846 13.6666C12.3013 13.8166 12.5346 13.9583 12.793 14.0583L12.9513 15.1583C12.968 15.2583 13.0513 15.3333 13.168 15.3333H14.8346C14.9263 15.3333 15.018 15.2583 15.0346 15.1583L15.193 14.0583C15.443 13.9499 15.668 13.8166 15.893 13.6666L16.918 14.0833C17.0263 14.0833 17.1346 14.0833 17.193 13.9749L18.0263 12.5333C18.0763 12.4416 18.0513 12.3333 17.9763 12.2666L17.0846 11.5833C17.1013 11.4416 17.118 11.3083 17.118 11.1666C17.118 11.0249 17.1096 10.8916 17.0846 10.7499L17.968 10.0666C18.043 9.99992 18.068 9.89158 18.018 9.79992L17.1846 8.35825C17.1346 8.24992 17.0263 8.24992 16.918 8.24992L15.893 8.66658C15.668 8.51659 15.443 8.37492 15.1846 8.27492L15.0263 7.17492C15.018 7.07492 14.9263 6.99992 14.8346 6.99992H13.168ZM7.33464 8.66658C3.6513 8.66658 0.667969 10.1583 0.667969 11.9999V13.6666H8.73464C8.36272 12.8856 8.16915 12.0316 8.16797 11.1666C8.16966 10.3293 8.35156 9.50228 8.7013 8.74158C8.25964 8.69159 7.8013 8.66658 7.33464 8.66658ZM14.0013 9.91658C14.693 9.91658 15.2513 10.4749 15.2513 11.1666C15.2513 11.8583 14.693 12.4166 14.0013 12.4166C13.3013 12.4166 12.7513 11.8583 12.7513 11.1666C12.7513 10.4749 13.3096 9.91658 14.0013 9.91658Z"
              fill={color}
            />
          </svg>
        );
      case "notification":
        return (
          <svg width="28" height="28" viewBox="380 370 440 440" fill={color}>
            <path d="M608.77,849.41h-17.54c-33.07-5.1-60.48-28.26-67.53-62.35h152.59c-7.06,34.13-34.43,57.23-67.53,62.35Z" />
            <path d="M771.75,766.28l-343.02.02c-15.79,0-28.96-9.96-34.02-23.14-5.86-15.27-1.27-31.1,10.81-41.47,31.02-26.61,48.84-64.56,49-105.5l.25-64.26c.27-70.07,55.33-127.86,124.29-138.09l.24-22.84c.12-11.42,9.3-20.14,20.24-20.41,10.48-.26,20.84,8.02,21.02,19.42l.38,23.83c69.69,10.27,124.13,69.25,124.37,140.04l.22,63.26c.14,40.97,18.52,78.57,49.44,105.07,11.83,10.14,16.04,26.04,10.3,40.92-4.9,12.68-17.56,23.14-33.52,23.15Z" />
          </svg>
        );
      case "notification-new":
        return (
          <svg width="28" height="28" viewBox="380 370 440 440" fill={color}>
            <path d="M612,849.41h-17.55c-35.69-5.31-64.44-30.18-71.91-66.07l161.69.04c-7.75,36.89-37.19,61.01-72.23,66.03Z" />
            <path d="M784.57,761.24l-361.31.05c-12.93,0-24.88-6.07-32.39-15.77-12.96-16.73-9.09-39.48,6.58-52.86,33.1-28.26,51.71-69.04,51.75-112.65l-.03-59.51c-.05-91.76,78.21-163.8,168.92-154.79-27.46,58.41-15.55,127.07,29.25,172.84,28.98,29.6,68.24,46.6,110.54,46.51,1.28,43,20.64,81.76,52.84,109.11,14.4,12.23,16.64,32.8,7.33,48.6-7.77,10.4-19.03,18.48-33.48,18.49Z" />
            <path d="M747.3,321.3c61.42-5.81,114.73,39.88,119.99,100.05,5.25,60.04-39.1,113.41-99.03,119.2-60.62,5.85-114.64-38.67-120.34-99.38-5.61-59.73,38.11-114.07,99.38-119.87Z" fill="#8e44ac" />
          </svg>
        );
      case "favorites":
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill={color}>
            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
          </svg>
        );
      case "search":
        return (
          <svg width="28" height="28" viewBox="320 320 560 560" fill={color}>
            <path d="M655.84,358.1c.93.56,2.78.67,4.37.78,97.02,6.65,174.21,84.06,181.06,180.98,4.13,58.51-17.01,114.86-59.2,155.65-62.9,60.82-157.45,72.57-232.95,27.78-2.79-1.66-7.94-2.81-10.89.14l-101.39,101.26c-7.57,7.56-16.52,12.39-26.29,15.42-25.03,7.74-47.31-10.73-52.33-33.22v-11.34c3.05-11.95,7.84-23,17.15-32.32l101.72-101.83c2.69-2.7,1.1-8.04-.32-10.45-44.74-75.49-33.04-170.09,27.79-232.94,31.29-32.32,72.44-53.27,117.83-58.38l13.6-1.53h19.84ZM640.69,409.54c-81.04,2.84-143.19,71.19-139.65,151.13,3.52,79.6,70.62,141.53,150.48,138.39,79.69-3.13,141.94-70.13,139.05-150.12-2.86-79.14-69.3-142.23-149.88-139.4Z" />
            <path d="M688.15,483.95c-44.98-18.5-95.08,2.71-114.04,46.75-4.28,9.95-15.92,14.07-25.33,10.61-9.87-3.63-16.2-15.15-12.07-25.34,26.58-65.66,102.28-96.69,167.45-69.15,10.38,4.39,15.13,16.09,10.75,26.46-4.21,9.96-15.66,15.25-26.76,10.68Z" />
          </svg>
        );
      case "chat":
        return (
          <svg width="28" height="28" viewBox="380 375 440 440" fill={color}>
            <path d="M606.33,735.74l-110.29,73.54c-6.45,4.3-13.6,4.99-20.27,1.66-6.31-3.15-10.6-9.67-10.6-17.53l-.03-59.58c-44.84-9.38-77.23-48.48-77.27-94.49l-.12-153.73c-.04-54.49,43.44-97.85,97.86-97.85h229.7c53.89,0,97.75,42.78,97.74,96.94l-.04,154.54c-.01,53.98-43.98,96.63-97.66,96.59l-109.03-.08ZM716.81,542.43c11.8,0,19.33-10.21,18.85-20.26-.47-9.97-8.48-18.34-19.37-18.34h-231.67c-11.22,0-19.37,8.93-19.41,19.18-.04,10.44,8.09,19.46,19.4,19.46l232.2-.03ZM676.98,581.13h-153.46c-11.46.01-19.87,8.93-19.66,19.72.21,10.4,8.49,18.92,19.66,18.92h153.87c11.44,0,19.88-9,19.64-19.73-.24-10.49-8.38-18.91-20.05-18.91Z" />
          </svg>
        );
      case "ai-chat":
        return (
          <svg width="28" height="28" viewBox="0 0 24 24" fill={color}>
            <path d="M20 2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h4l4 4 4-4h4c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z" />
          </svg>
        );
      case "settings-gear":
        return (
          <svg width="16" height="16" viewBox="0 0 24 24" fill={color}>
            <path d="M19.43 12.98c.04-.32.07-.64.07-.98s-.03-.66-.07-.98l2.11-1.65c.19-.15.24-.42.12-.64l-2-3.46c-.12-.22-.39-.3-.61-.22l-2.49 1c-.52-.4-1.09-.74-1.71-.98L12.75 3.03c-.05-.23-.25-.4-.49-.4H9.75c-.24 0-.44.17-.49.4l-.42 2.41c-.62.24-1.19.58-1.71.98l-2.49-1c-.22-.09-.49 0-.61.22l-2 3.46c-.12.22-.07.49.12.64l2.11 1.65c-.04.32-.07.64-.07.98s.03.66.07.98l-2.11 1.65c-.19.15-.24.42-.12.64l2 3.46c.12.22.39.3.61.22l2.49-1c.52.4 1.09.74 1.71.98l.42 2.41c.05.23.25.4.49.4h2.5c.24 0 .44-.17.49-.4l.42-2.41c.62-.24 1.19-.58 1.71-.98l2.49 1c.22.09.49 0 .61-.22l2-3.46c.12-.22.07-.49-.12-.64l-2.11-1.65zM12 15.5c-1.93 0-3.5-1.57-3.5-3.5s1.57-3.5 3.5-3.5 3.5 1.57 3.5 3.5-1.57 3.5-3.5 3.5z" />
          </svg>
        );
      case "support":
        return (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 11h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-5Zm0 0a9 9 0 1 1 18 0m0 0v5a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3Z" />
            <path d="M21 16v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v2" style={{ display: 'none' }} />
          </svg>
        );
      case "log-out":
        return (
          <svg width="16" height="16" viewBox="0 0 24 24" fill={color}>
            <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z" />
          </svg>
        );
      case "chevron-down":
        return (
          <svg width="20" height="24" viewBox="0 0 24 24" fill={color}>
            <path d="M7 10l5 5 5-5z" />
          </svg>
        );
      case "group":
        return (
          <svg width="26" height="26" viewBox="330 330 540 540" fill={color}>
            <path d="M470.45,869.79h-128.35c-5.62-3.07-11.71-7.1-11.7-14.8l.28-133.66c.05-23.44,18.83-40.8,40.17-45.98,34.46,30.71,85.7,30.61,119.96-.23,8.76,2.68,16.84,6.57,23.83,12.36l5.07,12.18c-31.55,12.47-51.81,42.16-51.77,76.41l.09,80.04c0,4.74,1.52,9.55,2.42,13.68Z" />
            <path d="M869.6,717.08v140.96c-2.6,5.42-6.33,9.18-11.76,11.76h-128.35c1.57-6.02,2.53-12.22,2.52-18.99l-.11-75.77c-.05-33.64-20.3-62.75-51.51-75.36l4.89-11.98c6.94-6.36,15.1-9.68,23.9-12.54,34.37,31.08,86.02,30.73,120.2-.06l7.02,2.58c18.02,6.62,29.07,21.38,33.2,39.41Z" />
            <path d="M688.31,869.79h-176.68c-5.79-2.77-11.99-7.09-11.99-14.7l-.03-80.06c0-20.58,12.62-38.41,31.4-45.8l8.77-3.45c34.49,31.34,86.47,30.83,120.66-.11l8.45,3.45c18.78,7.67,31.41,25.17,31.43,45.93l.09,77.88c0,8.46-4.86,13.89-12.1,16.87Z" />
            <path d="M724.35,447.13l-113.28,113.42c-6.2,6.21-16.02,6.18-22.21-.01l-113-113.05c-27.23-27.24-25.82-71.32.99-97.35,27.81-27,70.82-25.59,98.42,2.07l24.7,24.74,28.05-27.75c26.89-26.6,69.94-24.59,96.1,1.99,25.45,25.86,27.04,69.11.23,95.96Z" />
            <path d="M761.11,551.24c32.79-4.57,61.5,18.57,65.58,50.19,4.04,31.29-18.12,60.34-49.45,64.75-31.69,4.45-61.23-17.62-65.65-49.55-4.29-30.96,16.89-60.84,49.51-65.39Z" />
            <path d="M421.96,551.27c32.43-4.93,61.31,17.49,66.1,48.52s-17.06,61.57-48.93,66.28c-31.21,4.61-60.78-16.81-65.78-48.34-4.93-31.05,15.9-61.49,48.61-66.46Z" />
            <path d="M593.61,601.76c32.58-3.58,60.35,20.05,63.94,50.97,3.71,32-19.38,60.68-50.87,64.36s-60.41-18.93-64.37-50.27c-4.02-31.78,18.27-61.42,51.3-65.05Z" />
            <path d="M810.39,377.26c-8.36,4.17-17.52.55-21.31-6.69-3.86-7.38-1.33-17.22,6.55-21.19l33.57-16.91c8.19-4.12,17.71-1.17,21.75,6.82,3.84,7.61.87,17.29-7.35,21.4l-33.2,16.58Z" />
            <path d="M403.99,349.16c8.28,4.15,10.69,13.97,6.85,21.42-3.7,7.17-12.96,10.82-20.85,6.86l-34.37-17.22c-7.9-3.96-10.23-13.86-6.63-20.95,3.79-7.46,13.11-11.09,21.02-7.13l33.99,17.02Z" />
            <path d="M842.97,467.02c8.19,4.05,11.49,12.47,8.67,20.19-2.83,7.72-12.49,13.09-20.65,9.05l-35.09-17.38c-7.76-3.85-10.46-13.11-7.3-20.36,3.39-7.78,12.91-12.02,21.04-8l33.34,16.51Z" />
            <path d="M369.87,495.93c-8.21,4.09-17.38-.06-20.96-7.3-3.63-7.34-.98-16.99,7.03-20.98l34.37-17.14c7.8-3.89,17.03-.08,20.7,7.31,3.6,7.25,1.08,17.07-6.97,21.08l-34.17,17.04Z" />
          </svg>
        );
      case "adoption":
        return (
          <svg
            width="28"
            height="28"
            viewBox="410 385 440 440"
            fill={color}
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M797.12,782.47h-360.23c-6.99,0-11.35-4.92-11.25-11.8l.03-207.51,191.19-95.56,191.1,95.54.02,208.5c-.48,6.28-3.97,10.3-10.86,10.83ZM600.73,603.83c6.48-14.23,3.24-31.46-7.51-42.16-4.66-4.64-10.71-6.77-17.12-5.43-4.69.97-9.79,4.75-12.42,10.23-6.74,14.03-3.72,31.87,7.4,42.8,4.63,4.55,10.51,6.58,17.07,5.23,4.46-.91,9.84-4.67,12.57-10.67ZM662.81,609c11.05-11.2,13.73-28.77,7.13-42.52-2.78-5.78-8.15-9.59-13.22-10.4-6.14-.99-11.9,1.09-16.36,5.54-10.95,10.95-13.95,28.56-7.31,42.56,2.64,5.56,7.91,9.43,12.42,10.33,6.53,1.31,12.49-.59,17.34-5.51ZM549.3,644.15c4.65-16.36-2.1-33.81-16.09-42.5-5.22-3.25-11.74-3.38-17.62-.5-14.7,7.2-14.21,38.27,5.59,52.17,5.1,3.58,11.12,4.82,17.03,2.68,4.25-1.55,9.36-5.73,11.09-11.85ZM712.77,653.1c20.13-14.65,19.54-45.91,4.61-52.29-5.85-2.5-11.95-2.25-16.98.83-14.48,8.89-21.18,27.43-15.7,43.56,1.91,5.63,7.11,9.74,11.51,11.06,5.64,1.7,11.43.58,16.57-3.16ZM664.79,718.11c13.2-8.71,17.78-24.31,12.19-39.52-9.16-24.93-34.24-41.48-60.64-41.27-26.28.21-51.2,16.98-59.95,42.02-5.38,15.38-.22,30.87,13.25,39.29,7.29,4.56,15.25,7.42,23.88,8.91,22,3.8,52.1,3.22,71.27-9.43Z" />
            <path d="M823.61,547.24l-206.74-103.41-207.89,103.95c-3.6,1.8-7.69.97-10.67-.91-2.43-1.54-4.53-5.36-4.53-8.91l.02-42.02c0-3.65,1.88-7.72,5.47-9.52l212.7-106.35c3.38-1.69,7.05-1.36,10.39.31l211.37,105.69c3.94,1.97,6.1,5.76,6.1,9.9l.02,41.97c0,3.52-2.13,7.38-4.47,8.91-3.55,2.31-7.18,2.7-11.77.4Z" />
          </svg>
        );
      default:
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill={color}>
            <circle cx="12" cy="12" r="10" />
          </svg>
        );
    }
  };

  return getIconSvg(iconName, iconColor);
};

// Navbar Component
// Using React.memo to prevent unnecessary re-renders
const Navbar = React.memo(({ userType: propUserType }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  // All state hooks first
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isChatDropdownOpen, setIsChatDropdownOpen] = useState(false);
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isMediumScreen, setIsMediumScreen] = useState(false);
  const [isUltraSmallScreen, setIsUltraSmallScreen] = useState(false);
  const [highlightNotificationId, setHighlightNotificationId] = useState(null);

  // Check for webview header preference
  const queryParams = new URLSearchParams(location.search);
  const isHeaderParam = queryParams.get("isHeader");

  // Persist isHeader preference to sessionStorage
  useEffect(() => {
    if (isHeaderParam !== null) {
      sessionStorage.setItem("webview_is_header", isHeaderParam);
    }
  }, [isHeaderParam]);

  const storedIsHeader = sessionStorage.getItem("webview_is_header");
  const shouldHideHeader = (isHeaderParam === "false" || storedIsHeader === "false") && isMobile;
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 768);
      // Medium screen: 769px to 1024px (inclusive)
      setIsMediumScreen(width >= 769 && width <= 1024);
      setIsUltraSmallScreen(width <= 425);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const [pendingFollowRequestsCount, setPendingFollowRequestsCount] =
    useState(0);

  // Ref to track if we've already reset notification count for this dropdown open
  const hasResetNotificationCountRef = useRef(false);
  const [isFetchingNotifications, setIsFetchingNotifications] = useState(false);
  const [hoveredNavItemIndex, setHoveredNavItemIndex] = useState(null);
  const [isSearchHovered, setIsSearchHovered] = useState(false);
  const [isNotificationIconHovered, setIsNotificationIconHovered] =
    useState(false);
  const [isChatIconHovered, setIsChatIconHovered] = useState(false);
  // Initialize AI Chat state from localStorage
  const [isAIChatOpen, setIsAIChatOpen] = useState(() => {
    const stored = localStorage.getItem("ai_chat_open");
    return stored === "true";
  });
  const [isAIChatMinimized, setIsAIChatMinimized] = useState(() => {
    const stored = localStorage.getItem("ai_chat_minimized");
    return stored === "true";
  });
  const [isAIChatIconHovered, setIsAIChatIconHovered] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [searchBarPosition, setSearchBarPosition] = useState({ top: 100, left: 0, width: 600 });
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const [isMobileSearchIconHovered, setIsMobileSearchIconHovered] = useState(false);
  const [isDesktopSearchVisible, setIsDesktopSearchVisible] = useState(true);
  // Firm access-denied toast — must be declared here (before any early returns)
  const [firmToast, setFirmToast] = useState(null);
  // const [isCartIconHovered, setIsCartIconHovered] = useState(false); // Commented out for now - will be used later

  // All refs
  const redirectedRef = useRef(false);
  const mountedRef = useRef(true);
  const mobileMenuRef = useRef(null);
  const searchInputRef = useRef(null);
  const searchDropdownRef = useRef(null);
  const mobileSearchDropdownRef = useRef(null);
  const searchBarContainerRef = useRef(null);

  // Hamburger menu handler with robust double-trigger prevention
  const handleHamburgerClick = useHamburgerMenu(
    () => setIsMobileMenuOpen((open) => !open)
  );

  // UNIFIED: Use single authentication hook
  const {
    user,
    isAuthenticated,
    isLoading,
    userCounts,
    getUserType,
    getDisplayName,
    getProfileImageUrl,
    getInitials,
    logout,
    updateNotificationCount,
  } = useAuth();

  // All useEffect hooks
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Persist AI Chat open state to localStorage
  useEffect(() => {
    localStorage.setItem("ai_chat_open", isAIChatOpen.toString());
  }, [isAIChatOpen]);

  // Persist AI Chat minimized state to localStorage
  useEffect(() => {
    localStorage.setItem("ai_chat_minimized", isAIChatMinimized.toString());
  }, [isAIChatMinimized]);

  // Minimize chat when navigating to a new page
  useEffect(() => {
    // If chat is open and not minimized, minimize it on navigation
    if (isAIChatOpen && !isAIChatMinimized) {
      setIsAIChatMinimized(true);
    }
  }, [location.pathname]); // Only depend on pathname to avoid unnecessary re-renders

  // Listen for chat badge updates
  useEffect(() => {
    const handleChatBadgeUpdate = (event) => {
      const { count } = event.detail;

      // Update the local state to trigger re-render
      // This will update the badge display
      if (mountedRef.current) {
        // Force a re-render by updating a state variable
        setHoveredNavItemIndex((prev) => (prev === null ? 0 : null));
      }
    };

    // Listen for chat badge update events
    window.addEventListener("chat-badge-update", handleChatBadgeUpdate);

    // Initialize chat badge count from localStorage
    const storedChatCount = localStorage.getItem("chat_badge_count");
    if (storedChatCount) {
      setPendingFollowRequestsCount(parseInt(storedChatCount, 10));
    }

    return () => {
      window.removeEventListener("chat-badge-update", handleChatBadgeUpdate);
    };
  }, []);

  // Listen for notification badge updates
  useEffect(() => {
    const handleNotificationBadgeUpdate = (event) => {
      const { count } = event.detail;

      // Update notification count using the updateNotificationCount function
      if (mountedRef.current && updateNotificationCount) {
        // Get current count and increment it
        const currentCount = userCounts?.notifications || 0;
        const newCount = currentCount + 1;
        updateNotificationCount(newCount);
      }
    };

    // Listen for notification badge update events
    window.addEventListener("notification-badge-update", handleNotificationBadgeUpdate);

    return () => {
      window.removeEventListener("notification-badge-update", handleNotificationBadgeUpdate);
    };
  }, [updateNotificationCount, userCounts?.notifications]);

  // Function to reset chat badge count
  const resetChatBadgeCount = () => {
    localStorage.removeItem("chat_badge_count");
  };

  useEffect(() => {
    if (redirectedRef.current || !mountedRef.current) {
      return;
    }

    if (!isLoading && !isAuthenticated) {
      redirectedRef.current = true;
      localStorage.setItem("intended_route", location.pathname);
      navigate("/auth/login", { replace: true });
    }
  }, [isLoading, isAuthenticated, navigate, location.pathname]);

  // Reset notification badge count when dropdown opens
  useEffect(() => {
    if (isNotificationOpen && updateNotificationCount) {
      // Only reset once per dropdown open session
      if (!hasResetNotificationCountRef.current) {
        const currentCount = userCounts?.notifications || 0;
        if (currentCount > 0) {
          updateNotificationCount(0);
          hasResetNotificationCountRef.current = true;
        }
      }
    } else {
      // Reset the flag when dropdown closes
      hasResetNotificationCountRef.current = false;
      // Clear highlight when dropdown closes
      setHighlightNotificationId(null);
    }
    // Only depend on isNotificationOpen and updateNotificationCount
    // Don't include userCounts?.notifications to prevent infinite loops
  }, [isNotificationOpen, updateNotificationCount]);

  // Listen for open-notification-dropdown event from toasts
  useEffect(() => {
    const handleOpenNotificationDropdown = (event) => {
      const { notificationId } = event.detail;

      // Set highlight notification ID and open dropdown
      setHighlightNotificationId(notificationId);
      setIsNotificationOpen(true);
    };

    window.addEventListener('open-notification-dropdown', handleOpenNotificationDropdown);

    return () => {
      window.removeEventListener('open-notification-dropdown', handleOpenNotificationDropdown);
    };
  }, []);

  // useEffect(() => {
  //   fetchPendingFollowRequestsCount()

  //   // const interval = setInterval(() => {
  //   //   fetchPendingFollowRequestsCount()
  //   // }, 600000)

  //   // return () => clearInterval(interval)
  // }, [fetchPendingFollowRequestsCount])

  // Firm access-toast listener — must be here (before any early returns)
  useEffect(() => {
    const handleFirmToast = (event) => {
      const { message } = event.detail || {};
      setFirmToast({
        message: message || 'This page is not available for Firm accounts.',
      });
      // Auto-dismiss after 4 seconds
      const timer = setTimeout(() => setFirmToast(null), 4000);
      return () => clearTimeout(timer);
    };
    window.addEventListener('show-access-toast', handleFirmToast);
    return () => window.removeEventListener('show-access-toast', handleFirmToast);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Skip if this is a touch event that might have a click event following
      if (event.type === 'touchstart' || event.type === 'touchend') {
        return;
      }

      if (
        isNotificationOpen &&
        !event.target.closest(".notification-container")
      ) {
        setIsNotificationOpen(false);
      }

      if (isProfileOpen && !event.target.closest(".profile-container")) {
        setIsProfileOpen(false);
      }

      // Mobile menu close on outside click - be more specific about the button
      const menuButton = event.target.closest(".navbar-menu-btn");
      const menuContainer = event.target.closest(".mobile-menu-container");

      if (
        isMobileMenuOpen &&
        !menuButton &&
        !menuContainer &&
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target)
      ) {
        setIsMobileMenuOpen(false);
      }
    };

    // Use passive listeners and add some delay to prevent conflicts
    document.addEventListener("mousedown", handleClickOutside, { passive: true });
    document.addEventListener("touchstart", handleClickOutside, { passive: true });

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [isNotificationOpen, isProfileOpen, isMobileMenuOpen]);

  // Client-side filtering function for search results
  const filterSearchResults = (results, query) => {
    if (!query || !query.trim()) {
      return results;
    }
    const searchLower = query.trim().toLowerCase();
    return results.filter((user) => {
      // Handle both API response formats: user_name/full_name and username/first_name/last_name
      const firstName = (user.first_name || "").toLowerCase();
      const lastName = (user.last_name || "").toLowerCase();
      const username = (user.username || user.user_name || "").toLowerCase();
      const fullName = (user.full_name || "").toLowerCase();

      // Check if search matches anywhere in first name, last name, username, or full name
      return (
        firstName.includes(searchLower) ||
        lastName.includes(searchLower) ||
        username.includes(searchLower) ||
        fullName.includes(searchLower)
      );
    });
  };

  // Handle search functionality - must be before conditional returns
  const handleSearch = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      setShowSearchDropdown(false);
      return;
    }

    setIsSearching(true);
    setShowSearchDropdown(true);

    try {
      const response = await userAPI.searchUsers({
        query: query.trim()
      });

      if (response.data.status === "success") {
        // Filter out ADMIN and CLINIC_MODERATOR users from search results
        // CLINIC_MODERATOR users should only appear in clinic team management
        const filteredResults = (response.data.data || []).filter(
          user =>
            user.user_type !== 'ADMIN' &&
            user.user_type !== 'admin' &&
            user.user_type !== 'CLINIC_MODERATOR' &&
            user.user_type !== 'clinic_moderator'
        );
        setSearchResults(filteredResults);
      } else {
        setSearchResults([]);
      }
    } catch (error) {
      console.error("Search error:", error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Debounce search - must be before conditional returns
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery) {
        handleSearch(searchQuery);
      } else {
        setSearchResults([]);
        setShowSearchDropdown(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  // Check if desktop search is visible (not hidden by CSS)
  useEffect(() => {
    const checkDesktopSearchVisibility = () => {
      if (searchBarContainerRef.current) {
        const parent = searchBarContainerRef.current.closest('.navbar-desktop-items');
        if (parent) {
          const style = window.getComputedStyle(parent);
          const isVisible = style.display !== 'none' &&
            style.visibility !== 'hidden' &&
            parent.offsetWidth > 0 &&
            parent.offsetHeight > 0;
          setIsDesktopSearchVisible(isVisible);
        } else {
          setIsDesktopSearchVisible(false);
        }
      } else {
        // If no desktop search container, assume it's hidden (mobile view)
        setIsDesktopSearchVisible(false);
      }
    };

    checkDesktopSearchVisibility();
    window.addEventListener("resize", checkDesktopSearchVisibility);

    // Also check on initial load after a short delay
    const timeoutId = setTimeout(checkDesktopSearchVisibility, 100);

    // Check when mobile search opens/closes
    const checkInterval = setInterval(checkDesktopSearchVisibility, 200);

    return () => {
      window.removeEventListener("resize", checkDesktopSearchVisibility);
      clearTimeout(timeoutId);
      clearInterval(checkInterval);
    };
  }, [isMobileSearchOpen]);

  // Update search bar position when dropdown is shown - must be before conditional returns
  useEffect(() => {
    if (showSearchDropdown && searchBarContainerRef.current && isDesktopSearchVisible) {
      const updatePosition = () => {
        if (searchBarContainerRef.current) {
          const rect = searchBarContainerRef.current.getBoundingClientRect();
          setSearchBarPosition({
            top: rect.bottom, // Use viewport-relative position for fixed positioning
            left: rect.left, // Use viewport-relative position for fixed positioning
            width: rect.width,
            height: rect.height,
          });
        }
      };

      updatePosition();
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);

      return () => {
        window.removeEventListener("scroll", updatePosition, true);
        window.removeEventListener("resize", updatePosition);
      };
    }
  }, [showSearchDropdown, isDesktopSearchVisible]);

  // Close dropdown when clicking outside - must be before conditional returns
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check desktop search dropdown
      if (
        isDesktopSearchVisible &&
        !isMobileSearchOpen &&
        searchDropdownRef.current &&
        !searchDropdownRef.current.contains(event.target) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target) &&
        !event.target.closest('.mobile-search-container')
      ) {
        setShowSearchDropdown(false);
      }

      // Check mobile search dropdown
      if (
        isMobileSearchOpen &&
        mobileSearchDropdownRef.current &&
        !mobileSearchDropdownRef.current.contains(event.target) &&
        !event.target.closest('.mobile-search-container') &&
        !event.target.closest('.mobile-search-input-container')
      ) {
        setShowSearchDropdown(false);
      }

      // Close mobile search if clicking outside
      if (
        isMobileSearchOpen &&
        !event.target.closest('.mobile-search-container') &&
        !event.target.closest('.mobile-search-input-container')
      ) {
        setIsMobileSearchOpen(false);
      }
    };

    if (showSearchDropdown || isMobileSearchOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showSearchDropdown, isMobileSearchOpen, isDesktopSearchVisible]);

  // Now handle conditional returns after all hooks
  if (isLoading) {
    return (
      <nav
        style={{
          width: "100%",
          minHeight: "80px",
          background: "#FFFFFF",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "16px 12px",
          position: "fixed",
          top: 0,
          left: 0,
          zIndex: 1000,
          borderBottom: "1px solid #F0F0F0",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            color: "#666",
            fontFamily: '"Red Hat Display", sans-serif',
          }}
        >
          <div
            style={{
              width: "20px",
              height: "20px",
              border: "2px solid #f3f3f3",
              borderTop: "2px solid #8548A8",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
            }}
          ></div>
          Loading...
        </div>
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </nav>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  if (!getUserType || !getDisplayName) {
    console.warn("Navbar: Required auth functions not available");
    return null;
  }

  const userType = getUserType();

  if (shouldHideHeader) {
    return null;
  }

  // Navigation function
  const handleNavigation = (path) => {
    // If already on the target path (especially /social), scroll to top
    if (path === "/social" && location.pathname === "/social") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    // Reset chat badge count when visiting chat page
    if (path === "/chat") {
      resetChatBadgeCount();
    }

    try {
      navigate(path);
    } catch (error) {
      console.error("Navigation failed:", error);
    }
  };

  const handleLogoutClick = () => {
    setIsProfileOpen(false);
    setIsLogoutModalOpen(true);
  };

  const handleLogout = async () => {
    try {
      setIsProfileOpen(false);
      setIsLogoutModalOpen(false);


      // Clear all session data and cookies
      const clearAllSessionData = () => {
        // Clear localStorage
        localStorage.clear();

        // Clear sessionStorage
        sessionStorage.clear();

        // Clear all cookies
        document.cookie.split(";").forEach(function (c) {
          document.cookie = c
            .replace(/^ +/, "")
            .replace(
              /=.*/,
              "=;expires=" + new Date().toUTCString() + ";path=/"
            );
        });

        // Clear specific auth-related cookies
        const cookiesToClear = [
          "access_token",
          "refresh_token",
          "user_data",
          "device_id",
          "last_login",
          "demo_mode",
          "intended_route",
          "lang",
          "theme",
          "preferences",
        ];

        cookiesToClear.forEach((cookieName) => {
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname}`;
        });

      };

      // Clear session data first
      clearAllSessionData();


      // Attempt API logout
      const success = await logout();

      if (success) {
      } else {
      }

      // Mark as redirected to prevent loops
      redirectedRef.current = true;

      // Force navigation with replace to prevent back button issues
      window.location.replace("/auth/login");
    } catch (error) {
      console.error("Navbar: Logout error:", error);

      // Clear session data even if API call fails
      localStorage.clear();
      sessionStorage.clear();
      document.cookie.split(";").forEach(function (c) {
        document.cookie = c
          .replace(/^ +/, "")
          .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
      });


      // Ensure redirection even if error occurs
      redirectedRef.current = true;
      window.location.replace("/auth/login");
    }
  };

  const handleNotificationClick = () => {
    if (isMobile) {
      // Navigate to notifications page on mobile
      const userTypeRoute = userType?.toLowerCase()?.replace(/_/g, '-') || 'pet-guardian';
      navigate(`/dashboard/${userTypeRoute}/notifications`);
    } else {
      // Show dropdown on desktop
      const willBeOpen = !isNotificationOpen;
      setIsChatDropdownOpen(false);
      setIsNotificationOpen(willBeOpen);

    }
  };

  const handleAIChatToggle = () => {
    const willBeOpen = !isAIChatOpen;
    setIsAIChatOpen(willBeOpen);
    if (willBeOpen && isAIChatMinimized) {
      setIsAIChatMinimized(false);
    }
  };

  // Safe display name and profile handling
  const displayName = getDisplayName() || "User";
  const profileImageUrl = getProfileImageUrl();
  const userInitials = getInitials() || "U";
  const userEmail = user?.email || t("navbar.No_Email");

  // Determine user type with fallback
  const getColorScheme = (userType) => {
    const type = userType?.toUpperCase();
    switch (type) {
      case "PET_GUARDIAN":
        return {
          primary: "#8548A8",
          gradient: "linear-gradient(135deg, #8548A8 0%, #B968C7 100%)",
          hover: "#7A4397",
          profileBg: "#B968C7",
          secondary: "#F3E8FF",
          accent: "#7A4397",
          light: "#FAF5FF",
          badge: "#E9D5FF",
        };
      case "VETERINARIAN":
      case "VET":
        return {
          primary: "#00D4B3",
          gradient: "linear-gradient(135deg, #00D4B3 0%, #1DE9B6 100%)",
          hover: "#00C4A7",
          profileBg: "#1DE9B6",
          secondary: "#E0FBDE",
          accent: "#00C4A7",
          light: "#F0FFF4",
          badge: "#C6F6D5",
        };
      case "SERVICE_PROVIDER":
      case "BUSINESS":
        return {
          primary: "#D65745",
          gradient: "linear-gradient(135deg, #D65745 0%, #FF6B6B 100%)",
          hover: "#C7493A",
          profileBg: "#FF6B6B",
          secondary: "#FEEBE7",
          accent: "#C7493A",
          light: "#FFF5F5",
          badge: "#FED7D7",
        };
      case "FIRM":
        return {
          primary: "#1B3A75",
          gradient: "linear-gradient(135deg, #1B3A75 0%, #2D5BBF 100%)",
          hover: "#162E5E",
          profileBg: "#2D5BBF",
          secondary: "#E8EEF8",
          accent: "#162E5E",
          light: "#F0F4FC",
          badge: "#C7D5ED",
        };
      default:
        return {
          primary: "#8548A8",
          gradient: "linear-gradient(135deg, #8548A8 0%, #B968C7 100%)",
          hover: "#7A4397",
          profileBg: "#B968C7",
          secondary: "#F3E8FF",
          accent: "#7A4397",
          light: "#FAF5FF",
          badge: "#E9D5FF",
        };
    }
  };

  const colors = getColorScheme(userType);
  const userId = user?.id || user?.user_id;

  // ─────────────────────────────────────────────────────────────────────────

  // Helper: translation with safe fallback (avoids showing raw keys like "navbar.Adoption")
  const trNav = (key, fallback) => {
    const value = t(key);
    return value === key ? (fallback || key) : value;
  };

  const navigationItems = [
    {
      icon: "social-media",
      label: trNav("navbar.Sulhafa_Connect", "Sulhafa Connect"),
      path: `/social`,
    },
    {
      icon: "adoption",
      label: trNav("navbar.Adoption", "Adoption"),
      path: `/adoption`,
    },
    // Medical Care — hidden for Firm accounts (Care page is restricted)
    ...(userType !== USER_TYPES.FIRM ? [{
      icon: userType === "VETERINARIAN" ? "medical-care-vet" : "medical-care-not-vet",
      label: t("navbar.Medical_Care"),
      path: userType === "PET_GUARDIAN"
        ? `/medical/dashboard/petguardian`
        : userType === "SERVICE_PROVIDER"
          ? `/medical/dashboard/petshopowner`
          : userType === "VETERINARIAN"
            ? `/medical/clinic/redirect-to-clinic`
            : `/medical/clinic/redirect-to-clinic`
    }] : []),
  ];

  return (
    <>
      {/* Firm access-denied toast */}
      {firmToast && (
        <div
          style={{
            position: 'fixed',
            bottom: '24px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: '#1B3A75',
            color: '#fff',
            padding: '12px 24px',
            borderRadius: '10px',
            fontSize: '14px',
            fontFamily: '"Red Hat Display", sans-serif',
            fontWeight: 500,
            boxShadow: '0 4px 20px rgba(27,58,117,0.35)',
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            animation: 'slideUpFirmToast 0.3s ease',
            maxWidth: '90vw',
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="#fff" strokeWidth="2"/>
            <path d="M12 8v4M12 16h.01" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          {firmToast.message}
          <style>{`
            @keyframes slideUpFirmToast {
              from { opacity: 0; transform: translateX(-50%) translateY(12px); }
              to   { opacity: 1; transform: translateX(-50%) translateY(0); }
            }
          `}</style>
        </div>
      )}
      <nav
        className="navbar"
        style={{
          '--navbar-primary-color': colors.primary,
        }}
      >
        {/* Flex container for logo and nav items */}
        {/* Logo Section */}
        <div
          className="navbar-logo-container"
          onClick={() => {
            const ut = getUserType();
            if (ut === 'ADMIN' || ut === 'CONTENT_ADMIN') {
              return navigate('/admin/dashboard');
            }
            navigate("/social");
          }}
          style={{ cursor: "pointer" }}
        >
          <img
            src="/icons/navbar/Sulhafa_Beta_Logo.png"
            alt="Sulhafa Logo"
            className="navbar-logo"
            onError={(e) => {
              e.target.style.display = "none";
              e.target.nextSibling &&
                (e.target.nextSibling.style.display = "block");
            }}
          />
        </div>


        {/* Search Bar in Center */}
        <div
          className="navbar-desktop-items"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "clamp(8px, 2vw, 24px)",
            flex: 1,
            justifyContent: "center",
            maxWidth: "800px",
            margin: "0 auto",
            overflow: "visible",
            visibility: "visible",
            position: "relative",
            zIndex: 10002,
          }}
        >
          <div
            ref={searchBarContainerRef}
            className="navbar-search-container"
            onMouseEnter={(e) => {
              setIsSearchHovered(true);
            }}
            onMouseLeave={(e) => {
              setIsSearchHovered(false);
            }}
          >
            <IconComponent
              iconName="search"
              isHovered={isSearchHovered}
              colors={colors}
              defaultColor="rgba(73, 73, 73, 0.6)"
            />
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={(e) => {
                setIsSearchHovered(true);
                if (searchQuery || searchResults.length > 0) {
                  setShowSearchDropdown(true);
                }
              }}
              onBlur={() => {
                setIsSearchHovered(false);
              }}
              placeholder={t("navbar.SearchUser") || "Search for Users..."}
              className="navbar-search-input"
            />
            {isSearching && (
              <div
                className="navbar-search-spinner"
                style={{
                  borderTopColor: colors.primary,
                }}
              />
            )}
          </div>

          {/* Search Results Dropdown - Only show for desktop search when not using mobile search */}
          {typeof document !== 'undefined' && isDesktopSearchVisible && !isMobileSearchOpen && showSearchDropdown && (searchResults.length > 0 || isSearching || searchQuery) &&
            createPortal(
              <div
                ref={searchDropdownRef}
                className="navbar-search-dropdown"
                style={{
                  top: `${(searchBarPosition?.top || 100) + 8}px`, // 8px gap below search bar
                  left: `${searchBarPosition?.left || 0}px`,
                  width: `${searchBarPosition?.width || 600}px`,
                  transform: 'none', // Ensure no transform is applied that could center it
                }}
              >
                {isSearching && searchResults.length === 0 ? (
                  <div className="navbar-search-empty">
                    Searching...
                  </div>
                ) : filterSearchResults(searchResults, searchQuery).length > 0 ? (
                  filterSearchResults(searchResults, searchQuery).map((result) => (
                    <div
                      key={result.user_id || result.id}
                      onClick={() => {
                        navigate(`/social/profile/${result.user_id || result.id}`);
                        setShowSearchDropdown(false);
                        setSearchQuery("");
                      }}
                      className="navbar-search-result-item"
                    >
                      <img
                        src={result.profile_image_url || result.image || getUserPlaceholderImage(result.gender)}
                        alt={`${result.first_name || ''} ${result.last_name || ''}`.trim() || result.name || result.username}
                        className="navbar-search-result-image"
                        style={{
                          borderColor: getColorScheme(result.user_type).primary,
                        }}
                        onError={(e) => {
                          e.target.src = getUserPlaceholderImage(result.gender);
                        }}
                      />
                      <div style={{ flex: 1 }}>
                        <div className="navbar-search-result-name">
                          {result.full_name || `${result.first_name || ''} ${result.last_name || ''}`.trim() || result.name || result.user_name || result.username}
                        </div>
                      </div>
                    </div>
                  ))
                ) : searchQuery && !isSearching ? (
                  <div className="navbar-search-empty">
                    No results found
                  </div>
                ) : null}
              </div>,
              document.body
            )}
        </div>



        {/* Right Side Icons - Language Toggle, Sulhafa Connect, Medical Care, Notifications, Chat, Profile, Hamburger */}
        {/* Cart icon commented out for now - will be used later */}
        <div
          className="navbar-right-icons"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px", // Consistent gap between all icons
            flexShrink: 0, // Prevent icons from shrinking
          }}
        >
          {/* Language Toggle */}
          {/* Sulhafa Connect and Medical Care - Moved from center */}
          {/* Hide navigation items when hamburger button is visible (screens <= 1300px) */}
          {navigationItems.map((item, index) => {
            if (getUserType() == USER_TYPES.CLINIC_MODERATOR) {
              return null;
            }
            // For medical-care, check if we're in any clinic operations section
            let isActive = false;
            const pathname = location.pathname;

            // Explicitly check for social-media first and exclude medical paths
            if (item.icon === "social-media") {
              // Only active on exact /social path or paths starting with /social/
              // Explicitly exclude ALL medical paths - this must be false on any medical page
              const isOnSocialPage = pathname === "/social" || pathname.startsWith("/social/");
              const isOnMedicalPage = pathname.startsWith("/medical");
              isActive = isOnSocialPage && !isOnMedicalPage;
            } else if (item.icon && item.icon.startsWith("medical-care")) {
              // Active if on medical-care path OR any clinic operations route
              isActive = pathname.startsWith(item.path) ||
                pathname.startsWith("/medical/clinic/appointment-management") ||
                pathname.startsWith("/medical/clinic/chat") ||
                pathname.startsWith("/reception") ||
                (pathname.startsWith("/medical/clinic") &&
                  (pathname.includes("/appointment-management") ||
                    pathname.includes("/chat") ||
                    pathname.includes("/dashboard"))) ||
                pathname.startsWith("/medical/dashboard");
            } else {
              // Use startsWith for more precise matching instead of includes
              isActive = pathname === item.path || pathname.startsWith(item.path + "/");
            }
            const isHovered = hoveredNavItemIndex === index;

            return (
              <div key={index} className="navbar-icon-container social-nav-container">
                <button
                  onClick={() => handleNavigation(item.path)}
                  onMouseEnter={() => setHoveredNavItemIndex(index)}
                  onMouseLeave={() => setHoveredNavItemIndex(null)}
                  className={`navbar-nav-item-btn ${isActive ? 'active' : ''} hide-on-mobile`}
                  style={{
                    color: (isHovered || isActive) ? colors.primary : "rgba(73, 73, 73, 0.6)",
                  }}
                  title={item.label}
                >
                  <IconComponent
                    iconName={item.icon}
                    isActive={isActive}
                    isHovered={isHovered}
                    colors={colors}
                  />
                </button>
              </div>
            );
          })}

          {/* Community Button - Only visible on medium screens (769px to 1024px) */}
          {isMediumScreen && (
            <button
              onClick={() => handleNavigation("/social/channels")}
              onMouseEnter={() => setHoveredNavItemIndex(-1)}
              onMouseLeave={() => setHoveredNavItemIndex(null)}
              className={`navbar-nav-item-btn ${location.pathname.includes("/social/channels") ? 'active' : ''}`}
              style={{
                display: 'flex',
                color: hoveredNavItemIndex === -1 || location.pathname.includes("/social/channels") ? colors.primary : "rgba(73, 73, 73, 0.6)",
              }}
            >
              <IconComponent
                iconName="group"
                isActive={location.pathname.includes("/social/channels")}
                isHovered={hoveredNavItemIndex === -1}
                colors={colors}
              />
              <span>{t("navbar.Communities") || "Communities"}</span>
            </button>
          )}

          {/* My Pets Button - Only visible on medium screens (769px to 1024px) */}
          {isMediumScreen && (
            <button
              onClick={() => handleNavigation("/dashboard/pets")}
              onMouseEnter={() => setHoveredNavItemIndex(-2)}
              onMouseLeave={() => setHoveredNavItemIndex(null)}
              className={`navbar-nav-item-btn ${location.pathname.includes("/dashboard/pets") || location.pathname.startsWith("/pets/") || location.pathname === "/pets" ? 'active' : ''}`}
              style={{
                display: 'flex',
                color: hoveredNavItemIndex === -2 || location.pathname.includes("/dashboard/pets") || location.pathname.startsWith("/pets/") || location.pathname === "/pets" ? colors.primary : "rgba(73, 73, 73, 0.6)",
              }}
            >
              <IconComponent
                iconName="my-pets"
                isActive={location.pathname.includes("/dashboard/pets") || location.pathname.startsWith("/pets/") || location.pathname === "/pets"}
                isHovered={hoveredNavItemIndex === -2}
                colors={colors}
              />
              <span>{t("navbar.My_Pets") || "My Pets"}</span>
            </button>
          )}

          {/* Mobile Search Icon - Only visible on smaller screens */}
          <div className="navbar-icon-container mobile-search-container hide-on-desktop">
            <button
              onClick={() => {
                setIsMobileSearchOpen(!isMobileSearchOpen);
                if (!isMobileSearchOpen && searchInputRef.current) {
                  setTimeout(() => searchInputRef.current?.focus(), 100);
                }
              }}
              onMouseEnter={() => setIsMobileSearchIconHovered(true)}
              onMouseLeave={() => setIsMobileSearchIconHovered(false)}
              className="navbar-icon-btn mobile-search-btn"
              title={t("navbar.SearchUser") || "Search"}
            >
              <IconComponent
                iconName="search"
                isHovered={isMobileSearchIconHovered || isMobileSearchOpen}
                colors={colors}
                defaultColor="rgba(73, 73, 73, 0.6)"
              />
            </button>

            {/* Mobile Search Input - Shows below icon when clicked */}
            {isMobileSearchOpen && (
              <div className="mobile-search-input-container">
                <div className="mobile-search-input-wrapper">
                  <IconComponent
                    iconName="search"
                    isHovered={false}
                    colors={colors}
                    defaultColor="rgba(73, 73, 73, 0.6)"
                  />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => {
                      if (searchQuery || searchResults.length > 0) {
                        setShowSearchDropdown(true);
                      }
                    }}
                    placeholder={t("navbar.SearchUser") || "Search for Users..."}
                    className="mobile-search-input"
                  />
                  {isSearching && (
                    <div
                      className="navbar-search-spinner"
                      style={{
                        borderTopColor: colors.primary,
                      }}
                    />
                  )}
                  <button
                    onClick={() => {
                      setIsMobileSearchOpen(false);
                      setSearchQuery("");
                      setShowSearchDropdown(false);
                    }}
                    className="mobile-search-close-btn"
                  >
                    ×
                  </button>
                </div>

                {/* Mobile Search Results Dropdown - Only show when mobile search is open */}
                {isMobileSearchOpen && showSearchDropdown && (searchResults.length > 0 || isSearching || searchQuery) && (
                  <div
                    ref={mobileSearchDropdownRef}
                    className="mobile-search-dropdown"
                  >
                    {isSearching && searchResults.length === 0 ? (
                      <div className="navbar-search-empty">
                        Searching...
                      </div>
                    ) : filterSearchResults(searchResults, searchQuery).length > 0 ? (
                      filterSearchResults(searchResults, searchQuery).map((result) => (
                        <div
                          key={result.user_id || result.id}
                          onClick={() => {
                            navigate(`/social/profile/${result.user_id || result.id}`);
                            setShowSearchDropdown(false);
                            setSearchQuery("");
                            setIsMobileSearchOpen(false);
                          }}
                          className="navbar-search-result-item"
                        >
                          <img
                            src={result.profile_image_url || result.image || getUserPlaceholderImage(result.gender)}
                            alt={`${result.first_name || ''} ${result.last_name || ''}`.trim() || result.name || result.username}
                            className="navbar-search-result-image"
                            style={{
                                borderColor: getColorScheme(result.user_type).primary,
                            }}
                            onError={(e) => {
                              e.target.src = getUserPlaceholderImage(result.gender);
                            }}
                          />
                          <div style={{ flex: 1 }}>
                            <div className="navbar-search-result-name">
                              {result.full_name || `${result.first_name || ''} ${result.last_name || ''}`.trim() || result.name || result.user_name || result.username}
                            </div>
                          </div>
                        </div>
                      ))
                    ) : searchQuery && !isSearching ? (
                      <div className="navbar-search-empty">
                        No results found
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Notification Icon */}
          <div className="navbar-icon-container notification-container">
            <button
              onClick={handleNotificationClick}
              onMouseEnter={() => setIsNotificationIconHovered(true)}
              onMouseLeave={() => setIsNotificationIconHovered(false)}
              className="navbar-icon-btn"
              title={t("navbar.Notifications")}
            >
              {isFetchingNotifications ? (
                <div
                  className="navbar-notification-spinner"
                  style={{
                    borderTopColor: colors.primary,
                  }}
                />
              ) : (
                <IconComponent
                  iconName={userCounts?.notifications > 0 ? "notification-new" : "notification"}
                  isHovered={isNotificationIconHovered}
                  colors={colors}
                  defaultColor="rgba(73, 73, 73, 0.6)"
                />
              )}
              {/* Notification badge - show for pending follow requests */}
              {(
                userCounts?.notifications > 0) && (
                  <div className="navbar-badge">
                    {userCounts?.notifications}
                  </div>
                )}
            </button>

            {/* Notification Dropdown - Desktop only */}
            {!isMobile && isNotificationOpen && (
              <div
                className="dropdown-overlay"
                onClick={() => setIsNotificationOpen(false)}
              />
            )}
            {!isMobile && (
              <NotificationDropdown
                isOpen={isNotificationOpen}
                onClose={() => {
                  setIsNotificationOpen(false);
                  setHighlightNotificationId(null);
                }}
                userType={userType}
                colors={colors}
                // onRequestHandled={fetchNotifications}
                updateNotificationCount={updateNotificationCount}
                highlightNotificationId={highlightNotificationId}
              />
            )}
          </div>

          {/* Chat Icon */}
          <div className="navbar-icon-container">
            {(() => {
              const isChatActive = location.pathname.startsWith("/chat") || location.pathname.includes("/chat");
              return (
                <button
                  onClick={() => {
                    if (isMobile) {
                      // Navigate to chat page on mobile
                      navigate('/chat');
                    } else {
                      // Show dropdown on desktop
                      const willBeOpen = !isChatDropdownOpen;
                      setIsNotificationOpen(false);
                      setIsChatDropdownOpen(willBeOpen);
                    }
                  }}
                  onMouseEnter={() => setIsChatIconHovered(true)}
                  onMouseLeave={() => setIsChatIconHovered(false)}
                  className={`navbar-icon-btn ${isChatActive ? 'active' : ''}`}
                  title="Messages"
                >
                  <IconComponent
                    iconName="chat"
                    isActive={isChatActive}
                    isHovered={isChatIconHovered}
                    colors={colors}
                    defaultColor="rgba(73, 73, 73, 0.6)"
                  />
                </button>
              );
            })()}

            {/* Chat Badge */}
            {(() => {
              const chatBadgeCount = parseInt(
                localStorage.getItem("chat_badge_count") || "0"
              );
              return chatBadgeCount > 0 ? (
                <div className="navbar-badge navbar-chat-badge">
                  {chatBadgeCount > 99 ? "99+" : chatBadgeCount}
                </div>
              ) : null;
            })()}

            {/* Chat Dropdown - Desktop only */}
            {!isMobile && isChatDropdownOpen && (
              <div
                className="dropdown-overlay"
                onClick={() => setIsChatDropdownOpen(false)}
              />
            )}
            {!isMobile && (
              <ChatDropdown
                isOpen={isChatDropdownOpen}
                onClose={() => setIsChatDropdownOpen(false)}
                colors={colors}
              />
            )}
          </div>
          {/* AI Chatbot Icon */}
          {/* Hidden for now*/}
          <div className="navbar-icon-container navbar-ai-chat-icon">
            <button
              onClick={handleAIChatToggle}
              onMouseEnter={() => setIsAIChatIconHovered(true)}
              onMouseLeave={() => setIsAIChatIconHovered(false)}
              className={`navbar-icon-btn ${isAIChatOpen ? 'active' : ''}`}
              title={t("navbar.AI_Chat") || "AI Assistant"}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "8px",
                background: "transparent",
                border: "none",
                cursor: "pointer",
                minWidth: "44px",
                minHeight: "44px",
              }}
            >
              <img
                src="/images/yana-logo-nobg.png"
                alt="AI Assistant"
                style={{
                  width: "28px",
                  height: "28px",
                  objectFit: "contain",
                  opacity: isAIChatOpen ? 1 : 0.6,
                  transition: "opacity 0.2s ease",
                }}
              />
            </button>
          </div>


          {/* Shopping Cart Icon - Commented out for now - will be used later */}
          {/* 
          <button
            onClick={() => handleNavigation(`/dashboard/${userType}/cart`)}
            onMouseEnter={() => setIsCartIconHovered(true)}
            onMouseLeave={() => setIsCartIconHovered(false)}
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              padding: "8px",
              borderRadius: "8px",
              transition: "all 0.2s ease",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
            }}
            title="Shopping Cart"
          >
            <IconComponent
              iconName="shopping-cart"
              isHovered={isCartIconHovered}
              colors={colors}
              defaultColor="rgba(73, 73, 73, 0.6)"
            />
            {userCounts?.cartItems > 0 && (
              <div
                style={{
                  position: "absolute",
                  top: "4px",
                  right: "4px",
                  background: colors.primary,
                  color: "white",
                  borderRadius: "50%",
                  minWidth: "18px",
                  height: "18px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "11px",
                  fontWeight: "600",
                  fontFamily:
                    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
                  border: "2px solid #ffffff",
                  zIndex: 10,
                }}
              >
                {userCounts.cartItems > 99 ? "99+" : userCounts.cartItems}
              </div>
            )}
          </button>
          */}

          {/* Profile */}
          <div className="navbar-profile-container profile-container">
            <button
              onClick={() => setIsProfileOpen(!isProfileOpen)}
              className="navbar-profile-btn"
            >
              <img
                src={profileImageUrl || getUserPlaceholderImage(user?.gender)}
                alt="User Profile"
                width={44}
                height={44}
                className="navbar-profile-image"
                style={{
                  borderColor: colors.primary,
                }}
                onError={(e) => {
                  e.target.src = getUserPlaceholderImage(user?.gender);
                }}
              />
            </button>

            {/* Profile Dropdown */}
            {isProfileOpen && (
              <>
                <div
                  className="dropdown-overlay"
                  onClick={() => setIsProfileOpen(false)}
                />
                <div className="navbar-profile-dropdown profile-dropdown">
                  {/* User Info */}
                  <div
                    className="navbar-profile-user-info"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      textAlign: "center",
                    }}
                  >
                    <div
                      className="navbar-profile-user-name"
                      style={{
                        fontSize: "15px",
                        fontWeight: 600,
                      }}
                    >
                      {displayName}
                    </div>
                    <div
                      className="navbar-profile-user-type"
                      style={{
                        color: colors.primary,
                        fontSize: "13px",
                        fontWeight: 500,
                        marginTop: "2px",
                      }}
                    >
                      {userType
                        .replace("_", " ")
                        .toLowerCase()
                        .replace(/\b\w/g, (l) => l.toUpperCase())}
                    </div>
                    {/* View Profile button directly under user type */}
                    <button
                      onClick={() => {
                        navigate("/social/profile");
                        setIsProfileOpen(false);
                      }}
                      className="navbar-profile-view-btn"
                      style={{
                        marginTop: "10px",
                        padding: "9px 28px",
                        borderRadius: "999px",
                        border: `1px solid ${colors.primary}`,
                        background: "transparent",
                        color: colors.primary,
                        fontSize: "13.5px",
                        fontWeight: 500,
                        cursor: "pointer",
                        alignSelf: "center",
                        minWidth: "180px",
                      }}
                    >
                      {t("navbar.View_Profile") || "View Profile"}
                    </button>
                  </div>

                  <button
                    onClick={() => {
                      navigate("/account/preferences");
                      setIsProfileOpen(false);
                    }}
                    className="navbar-dropdown-menu-item"
                  >
                    <IconComponent
                      iconName="settings-gear"
                      isActive={false}
                      colors={colors}
                      defaultColor="#494949"
                    />
                    {t("navbar.Account_Preferences")}
                  </button>

                  {/* Language Toggle in Dropdown */}
                  <button
                    onClick={() => {
                      const newLang = i18n.language === "en" ? "ar" : "en";
                      i18n.changeLanguage(newLang);
                      setIsProfileOpen(false);
                    }}
                    className="navbar-dropdown-menu-item"
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#494949"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="navbar-dropdown-menu-item-icon"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                    </svg>
                    <span>{i18n.language === "en" ? "Arabic" : "English"}</span>
                    <span className="navbar-dropdown-menu-item-text">
                      {i18n.language === "en" ? "AR" : "EN"}
                    </span>
                  </button>

                  <button
                    onClick={() => {
                      setIsSupportModalOpen(true);
                      setIsProfileOpen(false);
                    }}
                    className="navbar-dropdown-menu-item"
                  >
                    <IconComponent
                      iconName="support"
                      isActive={false}
                      colors={colors}
                      defaultColor="#494949"
                    />
                    {t("navbar.Support")}
                  </button>

                  <button
                    onClick={handleLogoutClick}
                    className="navbar-dropdown-menu-item"
                  >
                    <IconComponent
                      iconName="log-out"
                      isActive={false}
                      colors={colors}
                      defaultColor="#494949"
                    />
                    {t("navbar.Log_Out")}
                  </button>
                </div>
              </>
            )}
          </div>
          {/* Hamburger menu button for mobile (moved to far right) */}
          <button
            className="navbar-menu-btn"
            onClick={handleHamburgerClick}
            aria-label="Open menu"
          >
            <span>☰</span>
          </button>

          {/* Mobile menu container: add ref for outside click detection */}
          {isMobileMenuOpen && (
            <div
              ref={mobileMenuRef}
              className="mobile-menu-container"
            >
              {navigationItems.map((item, index) => {
                // My Pets active state logic - Commented out for now - moved to social sidebar
                // Special logic for "My Pets" - should be active on all pet-related pages
                let isActive = false;
                const pathname = location.pathname;

                // Explicitly check for social-media first and exclude medical paths
                if (item.icon === "social-media") {
                  // Only active on exact /social path or paths starting with /social/
                  // Explicitly exclude ALL medical paths - this must be false on any medical page
                  const isOnSocialPage = pathname === "/social" || pathname.startsWith("/social/");
                  const isOnMedicalPage = pathname.startsWith("/medical");
                  isActive = isOnSocialPage && !isOnMedicalPage;
                } else if (item.icon && item.icon.startsWith("medical-care")) {
                  // Active if on medical-care path OR any clinic operations route
                  isActive = pathname.startsWith(item.path) ||
                    pathname.startsWith("/medical/clinic/appointment-management") ||
                    pathname.startsWith("/medical/clinic/chat") ||
                    pathname.startsWith("/reception") ||
                    (pathname.startsWith("/medical/clinic") &&
                      (pathname.includes("/appointment-management") ||
                        pathname.includes("/chat") ||
                        pathname.includes("/dashboard"))) ||
                    pathname.startsWith("/medical/dashboard");
                } else {
                  // Use startsWith for more precise matching instead of includes
                  isActive = pathname === item.path || pathname.startsWith(item.path + "/");
                }
                return (
                  <button
                    key={index}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleNavigation(item.path);
                      setIsMobileMenuOpen(false);
                    }}
                    className={`mobile-menu-item ${isActive ? 'active' : ''}`}
                    style={{
                      color: isActive ? colors.primary : "#494949",
                    }}
                  >
                    <IconComponent
                      iconName={item.icon}
                      isActive={isActive}
                      colors={colors}
                    />
                    <span>{item.label}</span>
                  </button>
                );
              })}

              {/* Communities Button - Only visible on mobile (768px and below) */}
              {isMobile && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleNavigation("/social/channels");
                    setIsMobileMenuOpen(false);
                  }}
                  className={`mobile-menu-item ${location.pathname.includes("/social/channels") ? 'active' : ''}`}
                  style={{
                    color: location.pathname.includes("/social/channels") ? colors.primary : "#494949",
                  }}
                >
                  <IconComponent
                    iconName="group"
                    isActive={location.pathname.includes("/social/channels")}
                    colors={colors}
                  />
                  <span>{t("navbar.Communities") || "Communities"}</span>
                </button>
              )}

              {/* My Pets Button - Only visible on mobile (768px and below) */}
              {isMobile && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleNavigation("/dashboard/pets");
                    setIsMobileMenuOpen(false);
                  }}
                  className={`mobile-menu-item ${location.pathname.includes("/dashboard/pets") || location.pathname.startsWith("/pets/") || location.pathname === "/pets" ? 'active' : ''}`}
                  style={{
                    color: location.pathname.includes("/dashboard/pets") || location.pathname.startsWith("/pets/") || location.pathname === "/pets" ? colors.primary : "#494949",
                  }}
                >
                  <IconComponent
                    iconName="my-pets"
                    isActive={location.pathname.includes("/dashboard/pets") || location.pathname.startsWith("/pets/") || location.pathname === "/pets"}
                    colors={colors}
                  />
                  <span>{t("navbar.My_Pets") || "My Pets"}</span>
                </button>
              )}

              {/* AI Chat Shortcut - only for very small screens where the icon is hidden */}
              {isUltraSmallScreen && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleAIChatToggle();
                    setIsMobileMenuOpen(false);
                  }}
                  className={`mobile-menu-item ${isAIChatOpen ? 'active' : ''}`}
                  style={{
                    color: isAIChatOpen ? colors.primary : "#494949",
                  }}
                >
                  <img
                    src="/images/yana-logo-nobg.png"
                    alt="AI Assistant"
                    style={{
                      width: "28px",
                      height: "28px",
                      objectFit: "contain",
                    }}
                  />
                  <span>{t("navbar.AI_Chat") || "AI Assistant"}</span>
                </button>
              )}
            </div>
          )}
        </div>
      </nav>

      {/* Search Modal */}
      <SearchModal
        isOpen={isSearchModalOpen}
        onClose={() => setIsSearchModalOpen(false)}
        userType={userType}
      />

      {/* Logout Confirmation Modal */}
      <LogoutConfirmationModal
        isOpen={isLogoutModalOpen}
        onClose={() => setIsLogoutModalOpen(false)}
        onConfirm={handleLogout}
        userType={userType}
      />

      {/* AI Chat Widget */}
      <AIChatWidget
        isOpen={isAIChatOpen}
        onClose={() => {
          setIsAIChatOpen(false);
          setIsAIChatMinimized(false);
        }}
        isMinimized={isAIChatMinimized}
        onMinimize={() => setIsAIChatMinimized(!isAIChatMinimized)}
        userTypeColor={colors.primary}
        userType={userType}
      />

      {/* Support Modal */}
      <SupportModal
        isOpen={isSupportModalOpen}
        onClose={() => setIsSupportModalOpen(false)}
      />
      {/* Enhanced Responsive styles for navbar */}
      <style>{`
        /* Dropdown overlay to ensure proper z-index */
        .dropdown-overlay {
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          bottom: 0 !important;
          z-index: 9998 !important;
          background: transparent !important;
        }

        /* Mobile overlay with semi-transparent background - for other dropdowns */
        @media (max-width: 768px) {
          .dropdown-overlay {
            background: rgba(0, 0, 0, 0.4) !important;
            backdrop-filter: blur(2px);
          }
        }

        /* All dropdowns with highest z-index - above chat page elements */
        .navbar-dropdown,
        .profile-dropdown {
          z-index: 100000 !important;
          position: fixed !important;
        }

        /* Notification and Chat dropdowns specific styling - Desktop only */
        .notification-dropdown {
          z-index: 9999 !important;
          position: fixed !important;
        }

        /* Ensure chat dropdown appears above notifications */
        .chat-dropdown {
          z-index: 10000 !important;
          position: fixed !important;
        }

        /* Ensure notification container has proper stacking */
        .notification-container {
          z-index: 9999 !important;
          position: relative !important;
        }

        /* Responsive positioning for dropdowns - removed inline overrides */
        /* Responsive styles are now handled in the component CSS files */

        /* Large screens - show all navigation items */
        @media (min-width: 1501px) {
          .navbar-menu-btn {
            display: none !important;
          }
          .navbar-dropdown {
            display: none !important;
          }
          .navbar-desktop-items {
            gap: clamp(16px, 2.5vw, 28px) !important;
            max-width: 1000px !important;
          }
        }

        /* Medium screens - still show navigation if there's space */
        @media (min-width: 1025px) and (max-width: 1500px) {
          .navbar-menu-btn {
            display: none !important;
          }
          .navbar-dropdown {
            display: none !important;
          }
          .navbar-desktop-items {
            gap: clamp(8px, 1.8vw, 20px) !important;
            max-width: 900px !important;
          }
          .nav-item-btn {
            font-size: 11px !important;
            padding: 6px 4px !important;
          }
        }

        /* Tablet screens (768px - 1024px) - show navigation items with compact spacing */
        @media (min-width: 769px) and (max-width: 1024px) {
          .navbar-desktop-items {
            display: none !important;
          }
          .navbar-search-container {
            display: none !important;
          }
          .navbar-menu-btn {
            display: none !important;
          }
          .navbar-dropdown {
            display: none !important;
          }
          .hide-on-mobile {
            display: flex !important;
          }
          .navbar-nav-item-btn {
            font-size: 10px !important;
            padding: 5px 3px !important;
          }
          .navbar-nav-item-btn span {
            display: none !important;
          }
          .navbar-icon-btn-text {
            display: none !important;
          }
          .mobile-search-btn {
            display: flex !important;
          }
        }

        /* Smaller screens - hide nav items, show hamburger and mobile search */
        @media (max-width: 768px) {
          .navbar-desktop-items {
            display: none !important;
          }
          .navbar-search-container {
            display: none !important;
          }
          .navbar-menu-btn {
            display: inline-flex !important;
          }
          .mobile-search-btn {
            display: flex !important;
          }
          /* Hide navigation items (Sulhafa Connect, Medical Care) when hamburger is visible */
          .hide-on-mobile {
            display: none !important;
          }
          .navbar-nav-item-btn span {
            display: none !important;
          }
          .navbar-icon-btn-text {
            display: none !important;
          }
          .navbar-dropdown {
            right: clamp(8px, 2vw, 16px) !important;
            top: 75px !important;
            min-width: 200px !important;
            border: 1px solid #E5E7EB !important;
          }
        }

        /* Tablet screens - optimize spacing */
        @media (max-width: 768px) {
          nav {
            padding: 14px clamp(12px, 3vw, 24px) !important;
            min-height: 75px !important;
          }
          .navbar-right-icons {
            gap: 12px !important;
          }
          .navbar-right-icons > button,
          .navbar-right-icons > div {
            transform: scale(0.9);
          }
          .navbar-dropdown {
            top: 65px !important;
            right: 8px !important;
            min-width: 180px !important;
          }
        }

        /* Small mobile screens (425px and below) - extra compact design */
        @media (max-width: 425px) {
          nav {
            padding: 10px 12px !important;
            min-height: 65px !important;
          }
          .navbar-right-icons {
            gap: 8px !important;
          }
          .navbar-right-icons > button,
          .navbar-right-icons > div {
            transform: scale(0.8);
          }
          .navbar-icon-btn {
            padding: 6px !important;
          }
          .navbar-menu-btn {
            padding: 6px !important;
            transform: scale(0.9);
          }
          .navbar-dropdown {
            top: 58px !important;
            right: 6px !important;
            min-width: calc(100vw - 12px) !important;
            max-width: calc(100vw - 12px) !important;
            font-size: 13px !important;
          }
          .profile-dropdown {
            right: 6px !important;
            min-width: calc(100vw - 12px) !important;
            max-width: calc(100vw - 12px) !important;
            font-size: 13px !important;
          }
          .navbar-ai-chat-icon {
            display: none !important;
          }
        }

        /* Mobile screens - compact design */
        @media (max-width: 480px) {
          nav {
            padding: 12px 16px !important;
            min-height: 70px !important;
          }
          .navbar-right-icons {
            gap: 8px !important;
          }
          .navbar-right-icons > button,
          .navbar-right-icons > div {
            transform: scale(0.85);
          }
          .navbar-dropdown {
            top: 60px !important;
            right: 8px !important;
            min-width: 170px !important;
            font-size: 13px !important;
          }
          .profile-dropdown {
            right: 8px !important;
            min-width: 160px !important;
            font-size: 13px !important;
          }
        }

        /* Ultra-small screens - hide some icons and make others smaller */
        @media (max-width: 360px) {
          .navbar-right-icons > button,
          .navbar-right-icons > div {
            transform: scale(0.75);
          }
          .navbar-right-icons {
            gap: 6px !important;
          }
          /* Hide chat icon on very small screens */
          .navbar-right-icons > button:nth-child(4) {
            display: none !important;
          }
        }

        /* Prevent text selection and improve UX */
        .navbar-desktop-items {
          user-select: none;
        }

        .nav-item-btn {
          flex-shrink: 0;
          user-select: none;
        }

        /* Smooth transitions for responsive changes */
        .navbar-right-icons > * {
          transition: transform 0.2s ease;
        }

        /* Ensure dropdowns don't overflow viewport */
        .navbar-dropdown,
        .profile-dropdown {
          max-width: calc(100vw - 32px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
        }
      `}</style>
    </>
  );
});

Navbar.displayName = 'Navbar';

export default Navbar;

